import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  DatePicker,
  Form,
  Input,
  Modal,
  Select,
  Switch,
  message,
  Upload,
} from "antd";
import { InboxOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCampaignById,
  updateCampaign,
  addImpact,
} from "../../Redux/CampaignSlice";
import { Editor } from "@tinymce/tinymce-react";
import Cookies from "universal-cookie";

const { Option } = Select;
const { useForm } = Form;

const EditCampaign = () => {
  const { campaignId } = useParams();
  const editorRef = useRef(null);
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [authorizedDocuments, setAuthorizedDocuments] = useState([]);
  const [form] = useForm();
  const [impactForm] = useForm();
  const [newCauseForm] = useForm();
  const today = moment();
  const [open, setOpen] = useState(false);
  const [newCauseOpen, setNewCauseOpen] = useState(false);
  const [causes, setCauses] = useState([]);
  const [isIndividual, setIsIndividual] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  const dispatch = useDispatch();
  const { currentCampaign } = useSelector((state) => state.campaign);
  const cookies = new Cookies();
  const token = cookies.get("jwtToken");

  useEffect(() => {
    if (campaignId) {
      dispatch(fetchCampaignById(campaignId));
    }
  }, [dispatch, campaignId]);

  useEffect(() => {
    const fetchCauses = async () => {
      try {
        const response = await axios.get(
          `${import.meta.env.VITE_API_URL_DASHBOARD_URL}/cause`
        );
        if (response.data && Array.isArray(response.data.causes)) {
          setCauses(response.data.causes);
        } else {
          throw new Error("API response is not an array");
        }
      } catch (error) {
        message.error(error.response.data.error[0]);
      }
    };

    fetchCauses();
  }, []);

  useEffect(() => {
    if (currentCampaign) {
      form.setFieldsValue({
        name: currentCampaign.name,
        goal: currentCampaign.goal,
        cause: currentCampaign.cause._id, // Set cause _id here
        description: currentCampaign.description,
        startDate: moment(currentCampaign.dates.start_date),
        endDate: currentCampaign.dates.last_date
          ? moment(currentCampaign.dates.last_date)
          : null,
        isActive: currentCampaign.status === "active",
        donation_types: currentCampaign.donation_types,
      });

      if (currentCampaign.cover) {
        const coverDocuments = currentCampaign.cover.map((url, index) => ({
          url,
          uid: `${url}-${index}`,
        }));
        setDocuments(coverDocuments);
      }

      if (currentCampaign.documents) {
        const authorizedDocuments = currentCampaign.documents.map(
          (url, index) => ({ url, uid: `${url}-${index}` })
        );
        setAuthorizedDocuments(authorizedDocuments);
      }
    }
  }, [currentCampaign, form]);

  useEffect(() => {
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        setIsIndividual(decodedToken.role === "individual");
      } catch (error) {
        console.error("Failed to decode token:", error);
      }
    }
  }, [token]);

  const handleOk = () => {
    impactForm
      .validateFields()
      .then((values) => {
        dispatch(addImpact(values));
        message.success("Impact added successfully!");
        impactForm.resetFields();
        setOpen(false);
      })
      .catch((info) => {
        message.error("Please fill in the required fields.");
      });
  };

  const tinyMceContext = () => {
    if (editorRef.current) {
      return editorRef.current.getContent();
    }
  };

  const handleNewCauseOk = async () => {
    try {
      const values = await newCauseForm.validateFields();
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL_DASHBOARD_URL}/cause`,
        values
      );
      setCauses([...causes, response.data]);
      newCauseForm.resetFields();
      setNewCauseOpen(false);
      message.success("New cause added successfully!");
    } catch (error) {
      message.error("Failed to add new cause.");
    }
  };

  const uploadDocument = async (
    file,
    setLoading,
    setUploadSuccess,
    setMessage,
    setKey
  ) => {
    setLoading(true);
    const token = cookies.get("jwtToken");
    const filename = file.name;

    try {
      // Step 1: Get the pre-signed S3 bucket URL
      const uploadResponse = await axios.post(
        `${import.meta.env.VITE_API_URL}/upload/private_documents`,
        { filename },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const { url, key } = uploadResponse.data; // Capture the 'key' here

      // Step 2: Upload the file to the S3 bucket using fetch
      const s3UploadResponse = await fetch(url, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type,
        },
      });

      if (s3UploadResponse.ok) {
        message.success("Document uploaded successfully!");
        setUploadSuccess(true);
        setMessage(filename); // Use filename as the message
        setKey(key); // Set the key for campaignData
      } else {
        throw new Error("Failed to upload file to S3");
      }
    } catch (error) {
      console.error("Error uploading document:", error);
      message.error("An error occurred while uploading the document.");
      setUploadSuccess(false);
    }
    setLoading(false);
  };

  const handleDocumentUpload = async ({ file, onSuccess, onError }) => {
    setLoading(true);
    uploadDocument(
      file,
      setLoading,
      (success) => {
        if (success) {
          setDocuments((prevDocs) => [
            ...prevDocs,
            {
              url: `${import.meta.env.VITE_S3_BASE_URL}/${file.name}`,
              uid: file.uid,
            },
          ]);
          onSuccess(null, file);
        } else {
          onError(new Error("Failed to upload document"));
        }
      },
      (filename) => message.success(`${filename} uploaded successfully!`),
      (key) => setDocuments((prevDocs) => [...prevDocs, key]) // Store the key
    );
  };
  const handleAuthorizedDocumentUpload = async ({
    file,
    onSuccess,
    onError,
  }) => {
    setLoading(true);
    uploadDocument(
      file,
      setLoading,
      (success) => {
        if (success) {
          setAuthorizedDocuments((prevDocs) => [
            ...prevDocs,
            {
              url: `${import.meta.env.VITE_S3_BASE_URL}/${file.name}`,
              uid: file.uid,
            },
          ]);
          onSuccess(null, file);
        } else {
          onError(new Error("Failed to upload document"));
        }
      },
      (filename) => message.success(`${filename} uploaded successfully!`)
    );
  };

  const handleRemoveImage = (uid) => {
    const updatedDocuments = documents.filter((doc) => doc.uid !== uid);
    setDocuments(updatedDocuments);
  };

  const handleRemoveAuthorizedImage = (uid) => {
    const updatedAuthorizedDocuments = authorizedDocuments.filter(
      (doc) => doc.uid !== uid
    );
    setAuthorizedDocuments(updatedAuthorizedDocuments);
  };

  const onFinish = async (values) => {
    const formValues = {
      name: values.name,
      goal: values.goal,
      impacts: [],
      cause: values.cause,
      currency: "INR",
      description: tinyMceContext(),
      status: values.isActive ? "active" : "inactive",
      cover: documents.map((doc) => doc.url),
      documents: authorizedDocuments.map((doc) => doc.url),
      dates: {
        start_date: values.startDate
          ? values.startDate.toISOString()
          : moment().toISOString(),
        last_date: values.endDate ? values.endDate.toISOString() : null,
      },
      donation_types: values.donation_types,
    };

    try {
      await dispatch(updateCampaign({ id: campaignId, data: formValues }));
      message.success("Campaign updated successfully!");
      navigate("/dashboard/");
    } catch (error) {
      message.error("Failed to update campaign");
    }
  };

  const donationOptions = [
    { label: "Crowd Fund Raising", value: "Crowd Fund Raising" },
    { label: "Zakat", value: "Zakat" },
    { label: "Sadqa", value: "Sadqa" },
    { label: "Crowd Lending", value: "Crowd Lending" },
    { label: "Amanah", value: "Amanah" },
    { label: "Qarz e Hasna", value: "Qarz e Hasna" },
  ];

  return (
    <div className="bg-[#93c5fd] pt-10 ml-36 min-h-screen">
      <div className="max-w-4xl mx-auto bg-white p-8 rounded-lg shadow">
        <div className="form-container">
          <Form
            layout="vertical"
            onFinish={onFinish}
            form={form}
            className="fundraiser-form"
          >
            <h1 className="form-title">Edit Campaign</h1>
            <Form.Item
              label="Campaign Title"
              name="name"
              rules={[{ required: true, message: "Please enter a title" }]}
            >
              <Input placeholder="Enter title" />
            </Form.Item>

            <Form.Item
              label="Campaign Goal (INR)"
              name="goal"
              rules={[{ required: true, message: "Please enter a goal" }]}
            >
              <Input
                placeholder="Enter goal"
                onKeyPress={(event) => {
                  if (!/[0-9]/.test(event.key)) {
                    event.preventDefault();
                  }
                }}
              />
            </Form.Item>

            <Form.Item
              label="Select a Cause"
              name="cause"
              rules={[{ required: true, message: "Please select a cause" }]}
            >
              <Select
                placeholder="Select a cause"
                dropdownRender={(menu) => <div>{menu}</div>}
              >
                {causes.map((cause) => (
                  <Option key={cause._id} value={cause._id}>
                    {cause.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Campaign Description"
              name="description"
              rules={[
                { required: true, message: "Please enter a description" },
              ]}
            >
              <Editor
                apiKey="vle3luqfs1chxeoib73634clbofjnixkfjtyhrjbeggnd8xu"
                onInit={(evt, editor) => (editorRef.current = editor)}
                initialValue=""
                init={{
                  height: 300,
                  menubar: false,
                  plugins: [
                    "advlist autolink lists link image charmap print preview anchor",
                    "searchreplace visualblocks code fullscreen",
                    "insertdatetime media table paste code help wordcount",
                  ],
                  toolbar:
                    "undo redo | formatselect | bold italic backcolor | \
                     alignleft aligncenter alignright alignjustify | \
                     bullist numlist outdent indent | removeformat | help",
                }}
              />
            </Form.Item>
            <div className="grid grid-cols-2 gap-4">
              <Form.Item
                label="Campaign Start Date"
                name="startDate"
                rules={[
                  { required: true, message: "Please select a start date" },
                ]}
              >
                <DatePicker
                  format="YYYY-MM-DD"
                  disabled
                  className="w-full rounded-lg input-style"
                />
              </Form.Item>

              <Form.Item label="Campaign End Date" name="endDate">
                <DatePicker
                  format="YYYY-MM-DD"
                  className="w-full rounded-lg input-style"
                  disabledDate={(current) =>
                    current && current < today.startOf("day")
                  }
                />
              </Form.Item>
            </div>
            <Form.Item
              label="Select Donation Types"
              name="donation_types"
              rules={[
                {
                  required: true,
                  message: "Please select at least one donation type",
                },
              ]}
            >
              <Select mode="multiple" placeholder="Select donation types">
                {donationOptions.map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item label="Cover Images" name="cover">
              <Upload.Dragger
                name="files"
                customRequest={handleDocumentUpload}
                onRemove={(file) => handleRemoveImage(file.uid)}
                fileList={documents.map((doc) => ({
                  ...doc,
                  url: doc.url.toString(),
                }))}
                multiple
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                  Click or drag file to this area to upload
                </p>
              </Upload.Dragger>
            </Form.Item>

            {isIndividual && (
              <Form.Item label="Documents" name="documents">
                <Upload.Dragger
                  name="files"
                  customRequest={handleAuthorizedDocumentUpload}
                  onRemove={(file) => handleRemoveAuthorizedImage(file.uid)}
                  fileList={authorizedDocuments.map((doc) => ({
                    ...doc,
                    url: doc.url.toString(),
                  }))}
                  multiple
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">
                    Click or drag file to this area to upload
                  </p>
                </Upload.Dragger>
              </Form.Item>
            )}

            <Form.Item label="Active" name="isActive" valuePropName="checked">
              <Switch defaultChecked={currentCampaign?.status === "active"} />
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit">
                Update Campaign
              </Button>
            </Form.Item>
          </Form>

          <Modal
            title="Add New Impact"
            visible={open}
            onOk={handleOk}
            onCancel={() => setOpen(false)}
          >
            <Form form={impactForm} layout="vertical">
              <Form.Item
                label="Impact"
                name="impact"
                rules={[
                  { required: true, message: "Please enter impact details" },
                ]}
              >
                <Input.TextArea rows={4} />
              </Form.Item>
            </Form>
          </Modal>

          <Modal
            title="Add New Cause"
            visible={newCauseOpen}
            onOk={handleNewCauseOk}
            onCancel={() => setNewCauseOpen(false)}
          >
            <Form form={newCauseForm} layout="vertical">
              <Form.Item
                label="Cause Name"
                name="name"
                rules={[{ required: true, message: "Please enter cause name" }]}
              >
                <Input />
              </Form.Item>
            </Form>
          </Modal>
        </div>
      </div>
    </div>
  );
};

export default EditCampaign;
